import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, FingerprintIcon, Share2Icon, BrainIcon, BarChart3Icon } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { differenceIntro, differentiators, differenceFlow, differenceConclusion, type Bilingual, type DifferenceIconId } from '../data/differenceContent';
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.6,
    ease: EASE
  }
};
const DOT_FIELD: React.CSSProperties = {
  backgroundImage: 'radial-gradient(currentColor 1px, transparent 1px), radial-gradient(currentColor 1px, transparent 1px)',
  backgroundSize: '18px 18px, 26px 26px',
  backgroundPosition: '0 0, 9px 13px'
};
const ICONS: Record<DifferenceIconId, typeof FingerprintIcon> = {
  identity: FingerprintIcon,
  systems: Share2Icon,
  intelligence: BrainIcon,
  operations: BarChart3Icon
};
export function Difference() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (value: Bilingual) => isFR ? value.fr : value.en;
  return <section className="bg-white border-t border-[#E4E6EC] py-16 lg:py-20 px-4 sm:px-6" data-id="element-2975" data-mp="1t1i5px1a0uh1">
      <div className="max-w-[1280px] mx-auto" data-id="element-2976" data-mp="1o25jnei8brfi">
        {/* Header */}
        <motion.div {...reveal} className="max-w-3xl mx-auto text-center" data-id="element-2977" data-mp="1o3yqouwr8do1">
          <div className="flex items-center justify-center gap-3" data-id="element-2978" data-mp="10ctc0j2jvrfb">
            <span className="w-6 h-px bg-[#4F46E5]/40" aria-hidden="true" data-id="element-2979" data-mp="wbhja4odqq2w" />
            <p className="font-sans text-xs font-semibold tracking-[0.22em] text-[#4F46E5] uppercase" data-id="element-2980" data-mp="1aah574kicqvd">
              {pick(differenceIntro.eyebrow)}
            </p>
            <span className="w-6 h-px bg-[#4F46E5]/40" aria-hidden="true" data-id="element-2981" data-mp="15a2zvuaajv11" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.14] tracking-tight text-[#121212] mt-4" data-id="element-2982" data-mp="2lo2wav9xh2jj">
            <span className="block" data-id="element-2983" data-mp="2gqb6q7wr9sii">{pick(differenceIntro.headlineLead)}</span>
            <span className="block" data-id="element-2984" data-mp="2oqh4ooke59uk">
              {pick(differenceIntro.headlineBefore)}
              <span className="text-[#4F46E5]" data-id="element-2985" data-mp="2srstwszcrt28">
                {pick(differenceIntro.headlineAccent)}
              </span>
              {pick(differenceIntro.headlineAfter)}
            </span>
          </h2>
          <p className="font-sans text-base md:text-lg text-[#5B6478] leading-relaxed mt-4" data-id="element-2986" data-mp="32pp3hgcn6fy8">
            {pick(differenceIntro.intro)}
          </p>
        </motion.div>

        {/* Four differentiators */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12" data-id="element-2987" data-mp="2a2w00q7vb2kj">
          {differentiators.map((item, index) => {
          const Icon = ICONS[item.icon];
          return <motion.article key={item.num} {...reveal} transition={{
            duration: 0.6,
            delay: index * 0.06,
            ease: EASE
          }} className="flex flex-col rounded-2xl border border-[#E4E6EC] bg-white shadow-[0_1px_3px_rgba(18,18,18,0.05)] px-6 py-5" data-id="element-2988" data-mp="2jtgxubfpw32e" data-mpi={index}>
                <div className="flex items-start justify-between gap-3" data-id="element-2989" data-mp="1jpzqcr1y1p25">
                  <span className="w-12 h-12 rounded-full bg-[#4F46E5]/[0.07] flex items-center justify-center" data-id="element-2990" data-mp="1etsk0x5mybjo">
                    <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.75} aria-hidden="true" data-id="element-2991" data-mp="19w947stahqpm" />
                  </span>
                  <span className="font-sans text-sm text-[#9AA0B0]" data-id="element-2992" data-mp="10vazf8b11f5z">
                    {item.num}
                  </span>
                </div>

                <p className="font-sans text-[11px] font-semibold tracking-[0.13em] uppercase text-[#4F46E5] mt-4" data-id="element-2993" data-mp="r4q0fjrfy8mj">
                  {pick(item.label)}
                </p>

                <h3 className="font-serif text-[1.35rem] leading-[1.2] mt-2" data-id="element-2994" data-mp="h58v53g3pwr3">
                  <span className="block text-[#121212]" data-id="element-2995" data-mp="h7bp1ykdka13">
                    {pick(item.headlineLead)}
                  </span>
                  {pick(item.headlineMuted) && <span className="block text-[#121212]/45" data-id="element-2996" data-mp="11wmklqst7vt6">
                      {pick(item.headlineMuted)}
                    </span>}
                </h3>

                <p className="font-sans text-sm text-[#5B6478] leading-relaxed mt-3" data-id="element-2997" data-mp="t1q2g8r438t6">
                  {pick(item.body)}
                </p>

                <div className="mt-auto pt-4" data-id="element-2998" data-mp="j41w0lk58cv6">
                  <div className="border-t border-[#E4E6EC] pt-3" data-id="element-2999" data-mp="j3aak3k9fvim">
                    <p className="font-sans text-[10px] tracking-[0.13em] uppercase text-[#9AA0B0] leading-relaxed" data-id="element-3000" data-mp="o3o8gss5dp2x">
                      {pick(item.footer)}
                    </p>
                  </div>
                </div>
              </motion.article>;
        })}
        </div>

        {/* Differentiation flow */}
        <motion.div {...reveal} transition={{
        duration: 0.6,
        delay: 0.1,
        ease: EASE
      }} className="relative overflow-hidden rounded-2xl bg-[#4F46E5]/[0.045] px-6 py-10 lg:px-12 mt-4" data-id="element-3001" data-mp="24ipr8ue935ig">
          <span className="pointer-events-none absolute -left-20 -bottom-24 w-80 h-80 rounded-full text-[#4F46E5]/30 opacity-30" style={DOT_FIELD} aria-hidden="true" data-id="element-3002" data-mp="3t1ajw9mqy43r" />
          <span className="pointer-events-none absolute -right-20 -bottom-24 w-80 h-80 rounded-full text-[#4F46E5]/30 opacity-30" style={DOT_FIELD} aria-hidden="true" data-id="element-3003" data-mp="6nan1sjifbas" />

          <ol className="relative grid grid-cols-1 sm:grid-cols-2 lg:flex lg:items-start gap-6 lg:gap-2" data-id="element-3004" data-mp="bkswrl5cpuju">
            {differenceFlow.map((stage, index) => {
            const Icon = ICONS[stage.icon];
            return <React.Fragment key={stage.title.en} data-id="element-3005" data-mp="kma9qungpg4b" data-mpi={index}>
                  {index > 0 && <div className="hidden lg:flex items-center shrink-0 pt-6" aria-hidden="true" data-id="element-3006" data-mp="2ravn2sks0dof">
                      <ArrowRightIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2} data-id="element-3007" data-mp="q9flf13glkvn" />
                    </div>}
                  <li className="flex-1 min-w-0 text-center" data-id="element-3008" data-mp="107n0tfi3nuvj">
                    <span className="w-14 h-14 mx-auto rounded-full bg-white shadow-[0_1px_3px_rgba(18,18,18,0.08)] flex items-center justify-center" data-id="element-3009" data-mp="106bvvccx4h2u">
                      <Icon className="w-[1.4rem] h-[1.4rem] text-[#4F46E5]" strokeWidth={1.75} aria-hidden="true" data-id="element-3010" data-mp="198tl7znvdpu2" />
                    </span>
                    <div className="font-sans text-xs font-semibold tracking-[0.1em] uppercase text-[#4F46E5] mt-4" data-id="element-3011" data-mp="1iymvmxc9zywq">
                      {pick(stage.title)}
                    </div>
                    <p className="font-sans text-sm text-[#5B6478] mt-1.5" data-id="element-3012" data-mp="1rzujelh61hck">
                      {pick(stage.caption)}
                    </p>
                  </li>
                </React.Fragment>;
          })}
          </ol>

          {/* Brand lockup — same assets as the site header */}
          <div className="relative text-center mt-10" data-id="element-3013" data-mp="3cg2cju1ide9j">
            <p className="font-sans text-[1.75rem] lg:text-[1.9rem] text-[#121212] leading-snug flex flex-wrap items-center justify-center gap-y-1" data-id="element-3014" data-mp="38eyvx5bmze79">
              <img src="https://cdn.magicpatterns.com/uploads/kFQruRR3oauw7NNUdgFajn/Logo_BCITI_(5).png" alt="BCITI" className="h-14 lg:h-[3.85rem] w-auto object-contain" data-id="element-3015" data-mp="33hqamvv6yu1s" />
              <span className="mx-2 text-[#121212]/25 font-sans text-2xl font-light select-none" aria-hidden="true" data-id="element-3016" data-mp="1t2esglbw63">
                |
              </span>
              <img src="https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png" alt="ATNA" className="h-[3rem] lg:h-[3.3rem] w-auto object-contain" data-id="element-3017" data-mp="np13ey6zkj2b" />
              <span className="ml-2.5 font-serif text-[#4F46E5]" data-id="element-3018" data-mp="3nfufjwbmy96y">
                {pick(differenceConclusion.brandSuffix)}
              </span>
            </p>
            <p className="font-sans text-[11px] tracking-[0.18em] uppercase text-[#5B6478] mt-3" data-id="element-3019" data-mp="3s8neu4oc4pbn">
              {pick(differenceConclusion.caption)}
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}